import { Editor } from '@tinymce/tinymce-react';
import cx from '@vindr/utils/helpers/cx';
import useImmerDebounce from '@vindr/utils/hooks/useImmerDebounce';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import isEqual from 'lodash/isEqual';
import { memo, useEffect, useRef } from 'react';
import TextEditor from '../../ui/TextEditor';
import { BaseElement } from './components/elements/base';
import StructuredElement from './components/elements/structured';
import type { StructuredReportElement } from './components/elements/types';
import { uuidv4 } from '@vindr/utils/helpers/common.helper';

const UUID_editor = uuidv4();

const OutputPanel = ({
  sections,
  className,
  triggerRerender,
  refresh,
}: {
  sections: StructuredElement[];
  className?: string;
  triggerRerender: {
    element: StructuredReportElement;
    elementIds?: string[];
    sectionId?: string;
    value?: string;
    preventHighlight?: boolean;
  };
  refresh: number;
}) => {
  const editorRef = useRef<Editor>(null);

  const [renderedValues, setRenderedValues] = useImmerDebounce(
    sections.map((x) => x?.render?.()),
    0
  );

  useEffect(() => {
    setRenderedValues(sections.map((x) => x.render()));
  }, [sections, setRenderedValues]);

  useEffect(() => {
    const { element, preventHighlight } = triggerRerender;
    if (!(element instanceof BaseElement)) return;

    if (!preventHighlight) {
      sections.map((x) => x.setHighlight(false));
      element?.setHighlight(true);
    }
    setRenderedValues(sections.map((x) => x.render()));
  }, [sections, triggerRerender, refresh, setRenderedValues]);

  const { width } = useWindowSize();

  return (
    <TextEditor
      ref={editorRef}
      disabled
      className={cx('h-full min-h-[300px]', className)}
      height={'100%'}
      id={UUID_editor}
      value={renderedValues.join('')}
      initProps={{
        // height: width >= 1300 ? '100%' : '600px',
        menubar: false,
        statusbar: false,
        toolbar: false,
        readonly: true,
        forced_root_block: 'div',
        // content_style: 'body { font-family: "Times New Roman", Times, serif; } p { margin: 0 }',
      }}
    />
  );
};

export default memo(
  OutputPanel,
  (prev, next) =>
    (isEqual(prev.sections, next.sections) ||
      JSON.stringify(prev.sections) === JSON.stringify(next.sections)) &&
    isEqual(prev.triggerRerender, next.triggerRerender) &&
    isEqual(prev.refresh, next.refresh)
);
